/**
 * Voice Agents Studio (Iter 202b) — provider-agnostic AI voice agents.
 *
 * v1 delivers: Provider CRUD, Agent CRUD, chat-based dry-run simulator,
 * and ElevenLabs voice preview. Real telephony (outbound calls) will land
 * in a follow-up iteration once the user provides ElevenLabs Conversational
 * AI agent + phone number OR the MCube SIP-trunk equivalent.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mic, Bot, Plus, Trash2, Save, MessageSquare, PhoneCall, Wand2, Play, KeyRound, CheckCircle2, AlertCircle, Loader2, Rocket, PhoneOff, History, Sparkles, PhoneForwarded, X } from "lucide-react";
import { toast } from "sonner";
import { useConversation, ConversationProvider } from "@elevenlabs/react";
import { apiErrorMessage } from "@/lib/apiError";

const MISSIONS = [
  { id: "product_pitch", label: "Product Pitch" },
  { id: "offer_explainer", label: "Offer Explainer" },
  { id: "appointment_setter", label: "Appointment Setter" },
  { id: "follow_up", label: "Follow-up" },
  { id: "survey", label: "Survey" },
  { id: "custom", label: "Custom" },
];

export default function VoiceAgents() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return (t === "providers" || t === "agents" || t === "numbers") ? t : "agents";
    } catch { return "agents"; }
  });
  return (
    <div className="space-y-6" data-testid="voice-agents-page">
      <PageHeader
        title="Voice Agents"
        subtitle="Build AI voice agents that pitch, follow up, and book meetings. Provider-agnostic — swap ElevenLabs/MCube/Twilio without breaking your agents."
        icon={Mic}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="agents" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="va-tab-agents">
            <Bot className="w-4 h-4 mr-2" /> Agents
          </TabsTrigger>
          <TabsTrigger value="common_kb" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="va-tab-common-kb">
            <MessageSquare className="w-4 h-4 mr-2" /> Common Training
          </TabsTrigger>
          <TabsTrigger value="providers" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="va-tab-providers">
            <KeyRound className="w-4 h-4 mr-2" /> Providers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="agents" className="mt-6">
          <AgentsTab wsId={activeWorkspaceId} />
        </TabsContent>
        <TabsContent value="common_kb" className="mt-6">
          <CommonKbTab wsId={activeWorkspaceId} />
        </TabsContent>
        <TabsContent value="providers" className="mt-6">
          <ProvidersTab wsId={activeWorkspaceId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ─── Agents Tab ────────────────────────────────────────────────────

/** 7-day weighted performance score with a thumbs-up / thumbs-down
 *  arrow vs. the previous 7 days. Hover reveals current/previous
 *  booked-rate and connect-rate so operators can tell *why* the
 *  score moved. */
function WeeklyScoreBadge({ data }) {
  if (!data || data.current?.calls === 0) {
    return (
      <Badge variant="outline" className="text-[9px] text-muted-foreground" title="No calls in the last 7 days">
        — / 100
      </Badge>
    );
  }
  const trend = data.trend;
  const score = data.score ?? 0;
  const delta = data.delta ?? 0;
  const trendCls = trend === "up"   ? "bg-emerald-100 text-emerald-800 border-emerald-500/40"
                 : trend === "down" ? "bg-red-100 text-red-800 border-red-500/40"
                                     : "bg-slate-100 text-slate-700 border-slate-400/40";
  const arrow = trend === "up" ? "▲" : trend === "down" ? "▼" : "→";
  const title = (
    `7-day score: ${score}/100 (prev ${data.prev_score ?? 0})\n`
    + `Delta ${delta >= 0 ? "+" : ""}${delta} pts\n\n`
    + `This week: ${data.current.calls} calls · ${data.current.connect_rate}% connect · ${data.current.booked_rate}% booked\n`
    + `Last week: ${data.previous.calls} calls · ${data.previous.connect_rate}% connect · ${data.previous.booked_rate}% booked`
  );
  return (
    <Badge className={`text-[9px] border ${trendCls}`}
            data-testid="va-weekly-score"
            title={title}>
      {arrow} {score}/100
    </Badge>
  );
}


/** 24-bar hourly connect-rate sparkline for a single voice agent.
 *  Bars are coloured by connect-rate (empty hour = ultra-faint grey,
 *  <30% = red, <60% = amber, >=60% = emerald). Hover reveals the
 *  exact call / connected count for that hour. */
function ConnectRateSparkline({ data }) {
  const hourly = data?.hourly;
  const hasAny = hourly && (data?.total_calls ?? 0) > 0;
  // Always render a 24-bar strip so cards line up vertically.
  const now = new Date();
  const fmtHour = (idxFromNow) => {
    const d = new Date(now.getTime() - (23 - idxFromNow) * 3600 * 1000);
    return d.getHours().toString().padStart(2, "0") + ":00";
  };
  return (
    <div className="pt-1" title="Hourly connect-rate · last 24h">
      <div className="flex items-end gap-[1px] h-6" data-testid="va-heatmap-strip">
        {Array.from({ length: 24 }).map((_, i) => {
          const b = hourly ? hourly[i] : null;
          const rate = b?.connect_rate ?? 0;
          const calls = b?.calls ?? 0;
          // Bar height scales with volume (so quiet hours shrink)
          // and colour reflects quality.
          const minH = 2;
          const h = calls === 0 ? minH : Math.max(minH, Math.round(4 + rate * 0.18));
          let cls = "bg-muted-foreground/15";
          if (calls > 0) {
            if (rate >= 60) cls = "bg-emerald-500/80";
            else if (rate >= 30) cls = "bg-amber-500/80";
            else cls = "bg-red-500/70";
          }
          return (
            <div key={i}
                  className={`flex-1 min-w-[2px] rounded-[1px] ${cls}`}
                  style={{ height: `${h}px` }}
                  title={`${fmtHour(i)} · ${calls} call${calls === 1 ? "" : "s"}${calls ? ` · ${rate}% connect` : ""}`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-muted-foreground/70 mt-0.5">
        <span>−24h</span>
        {hasAny ? (
          <span>
            {data.total_calls} call{data.total_calls === 1 ? "" : "s"} ·{" "}
            {Math.round(100 * data.total_connected / data.total_calls)}% connect
          </span>
        ) : (
          <span className="italic">no calls yet</span>
        )}
        <span>now</span>
      </div>
    </div>
  );
}


function AgentsTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [simulating, setSimulating] = useState(null);
  const [callingAgent, setCallingAgent] = useState(null);
  const [historyAgent, setHistoryAgent] = useState(null);
  const [heatmap, setHeatmap] = useState({}); // {agent_id: {hourly, total_calls}}
  const [score, setScore] = useState({});     // {agent_id: {score, delta, trend, …}}
  const load = () => api.get(`/workspaces/${wsId}/voice-agents`)
    .then((r) => setRows(r.data.rows || []))
    .catch(() => setRows([]));
  const loadHeatmap = () => api.get(`/workspaces/${wsId}/voice-agents-heatmap-24h`)
    .then((r) => {
      const map = {};
      (r.data?.agents || []).forEach((a) => { map[a.agent_id] = a; });
      setHeatmap(map);
    })
    .catch(() => setHeatmap({}));
  const loadScore = () => api.get(`/workspaces/${wsId}/voice-agents-score-7d`)
    .then((r) => {
      const map = {};
      (r.data?.agents || []).forEach((a) => { map[a.agent_id] = a; });
      setScore(map);
    })
    .catch(() => setScore({}));
  useEffect(() => {
    if (!wsId) return;
    load();
    loadHeatmap();
    loadScore();
    // Refresh the heatmap every 2 minutes so an operator watching the
    // page sees the sparkline fill in real-time as calls land.
    const t = setInterval(loadHeatmap, 120000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [wsId]);

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    await api.delete(`/workspaces/${wsId}/voice-agents/${a.id}`);
    toast.success("Deleted");
    load();
  };

  const publish = async (a) => {
    if (!a.voice_id) { toast.error("Pick a voice first"); return; }
    const tid = toast.loading(a.convai_agent_id ? "Syncing to ElevenLabs…" : "Publishing to ElevenLabs…");
    try {
      await api.post(`/workspaces/${wsId}/voice-agents/${a.id}/publish`);
      // Feb 2026 · Publish now runs in the background. Poll for the
      // result so we don't blow Cloudflare's edge timeout.
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        const { data } = await api.get(`/workspaces/${wsId}/voice-agents/${a.id}/publish-status`);
        if (data.publish_status === "published") {
          toast.success(a.convai_agent_id ? "Synced to ElevenLabs" : "Published! You can now make a live call.", { id: tid });
          load();
          return;
        }
        if (data.publish_status === "failed") {
          toast.error(data.publish_error || "Publish failed", { id: tid });
          load();
          return;
        }
        if (attempts > 60) {
          toast.message("Still publishing… check back shortly.", { id: tid });
          load();
          return;
        }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Publish failed — check ElevenLabs key"), { id: tid });
    }
  };

  const unpublish = async (a) => {
    if (!window.confirm(`Unpublish "${a.name}" from ElevenLabs? Live calls will stop.`)) return;
    try {
      await api.post(`/workspaces/${wsId}/voice-agents/${a.id}/unpublish`);
      toast.success("Unpublished");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Unpublish failed")); }
  };

  if (rows === null) return <div className="text-sm text-muted-foreground" data-testid="va-loading">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {rows.length} agent{rows.length === 1 ? "" : "s"}
        </div>
        <Button className="rounded-none" onClick={() => setEditing({})} data-testid="va-new-agent">
          <Plus className="w-4 h-4 mr-2" /> New agent
        </Button>
      </div>

      {rows.length === 0 ? (
        <Card className="rounded-none border-dashed">
          <CardContent className="p-10 text-center text-sm text-muted-foreground space-y-3" data-testid="va-empty">
            <div className="text-4xl">🤖</div>
            <div>No voice agents yet. Create your first one — configure the persona, opening line, talking points, and knowledge base.</div>
            <div className="text-xs">You can dry-run in chat mode without connecting a phone provider.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((a) => (
            <Card key={a.id} className="rounded-none hover:border-primary transition-colors" data-testid={`va-agent-${a.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{a.name}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1 flex-wrap mt-0.5">
                      <Badge variant="outline" className="text-[10px]">
                        {(a.mission || "").replace(/_/g, " ")}
                      </Badge>
                      <span>·</span>
                      <span>{a.tone || "friendly"}</span>
                      {a.voice_name && <><span>·</span><span>🎙 {a.voice_name}</span></>}
                    </div>
                  </div>
                  {a.is_active ? (
                    <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-500/40">Active</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[9px]">Paused</Badge>
                  )}
                  <WeeklyScoreBadge data={score[a.id]} />
                </div>
                {a.description && (
                  <div className="text-xs text-muted-foreground line-clamp-2">
                    {a.description}
                  </div>
                )}
                {a.opening_line && (
                  <div className="text-[11px] italic text-foreground/70 line-clamp-2 border-l-2 border-primary/40 pl-2">
                    &ldquo;{a.opening_line}&rdquo;
                  </div>
                )}
                <ConnectRateSparkline data={heatmap[a.id]} />
                {a.convai_agent_id && (
                  <div className="text-[10px] text-emerald-700 flex items-center gap-1" data-testid={`va-published-${a.id}`}>
                    <Rocket className="w-3 h-3" /> Published to ElevenLabs
                  </div>
                )}
                {a.publish_status === "publishing" && (
                  <div className="text-[10px] text-amber-700 flex items-center gap-1" data-testid={`va-publishing-${a.id}`}>
                    <Loader2 className="w-3 h-3 animate-spin" /> Publishing to ElevenLabs…
                  </div>
                )}
                {a.publish_status === "failed" && a.publish_error && (
                  <div className="text-[10px] text-red-700 flex items-center gap-1" data-testid={`va-publish-failed-${a.id}`} title={a.publish_error}>
                    <AlertCircle className="w-3 h-3" /> Last publish failed — click Publish to retry.
                  </div>
                )}
                <div className="flex items-center gap-1 pt-2 border-t border-border">
                  <Button size="sm" variant="outline" className="rounded-none"
                           onClick={() => setSimulating(a)}
                           data-testid={`va-simulate-${a.id}`} title="Chat-based dry-run">
                    <MessageSquare className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none"
                           onClick={() => publish(a)}
                           data-testid={`va-publish-${a.id}`}
                           title={a.convai_agent_id ? "Sync latest edits to ElevenLabs" : "Publish to ElevenLabs to enable live calls"}>
                    <Rocket className="w-3 h-3 mr-1" /> {a.convai_agent_id ? "Sync" : "Publish"}
                  </Button>
                  {a.convai_agent_id && (
                    <>
                      <Button size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                               onClick={() => setCallingAgent(a)}
                               data-testid={`va-live-call-${a.id}`}>
                        <PhoneCall className="w-3 h-3 mr-1" /> Live call
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-none"
                               onClick={() => setHistoryAgent(a)}
                               data-testid={`va-history-${a.id}`} title="Call history">
                        <History className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" className="rounded-none"
                           onClick={() => setEditing(a)}
                           data-testid={`va-edit-${a.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none text-red-600 hover:text-red-700"
                           onClick={() => del(a)}
                           data-testid={`va-delete-${a.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <AgentEditor wsId={wsId} agent={editing}
                      onClose={() => setEditing(null)}
                      onSaved={() => { setEditing(null); load(); }} />
      )}
      {simulating && (
        <SimulatorDialog wsId={wsId} agent={simulating}
                          onClose={() => setSimulating(null)} />
      )}
      {callingAgent && (
        <LiveCallDialog wsId={wsId} agent={callingAgent}
                         onClose={() => setCallingAgent(null)} />
      )}
      {historyAgent && (
        <ConversationsDialog wsId={wsId} agent={historyAgent}
                              onClose={() => setHistoryAgent(null)} />
      )}
    </div>
  );
}


// ─── Live Call Dialog (ElevenLabs ConvAI over WebRTC) ─────────────

function LiveCallInner({ wsId, agent, onClose }) {
  const [signedUrl, setSignedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [transcript, setTranscript] = useState([]);

  const conversation = useConversation({
    onConnect: () => toast.success("Connected to " + agent.name),
    onDisconnect: () => toast.info("Call ended"),
    onError: (e) => {
      toast.error(typeof e === "string" ? e : (e?.message || "Voice error"));
      setError(typeof e === "string" ? e : (e?.message || "Voice error"));
    },
    onMessage: (m) => {
      // Append to local transcript view
      setTranscript((t) => [...t, {
        role: m.source === "user" ? "user" : "assistant",
        text: m.message || m.text || "",
        at: Date.now(),
      }]);
    },
  });

  const status = conversation.status;
  const isSpeaking = conversation.isSpeaking;

  useEffect(() => () => {
    // On close, end any active session
    if (conversation.status === "connected") {
      conversation.endSession().catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const startCall = async () => {
    setStarting(true);
    setError(null);
    try {
      // 1) Ask browser for mic permission — required for WebRTC
      await navigator.mediaDevices.getUserMedia({ audio: true });
      // 2) Fetch signed URL from backend
      const { data } = await api.get(`/workspaces/${wsId}/voice-agents/${agent.id}/signed-url`);
      setSignedUrl(data.signed_url);
      // 3) Start the ElevenLabs conversation
      await conversation.startSession({ signedUrl: data.signed_url });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to start call";
      setError(msg);
      toast.error(msg);
    } finally { setStarting(false); }
  };

  const endCall = async () => {
    try { await conversation.endSession(); } catch { /* noop */ }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { endCall(); onClose?.(); } }}>
      <DialogContent className="rounded-none max-w-xl" data-testid="va-live-call">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-emerald-600" /> Live call · {agent.name}
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Talk directly with the AI agent from your browser. Make sure your mic works.
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border p-4 text-center">
            {status === "disconnected" && !starting && (
              <>
                <div className="text-6xl mb-3">🎙</div>
                <div className="text-sm mb-3">Ready to speak with {agent.name}</div>
                <Button className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
                         onClick={startCall} data-testid="va-live-start">
                  <PhoneCall className="w-4 h-4 mr-2" /> Start call
                </Button>
              </>
            )}
            {starting && (
              <div className="text-sm text-muted-foreground flex items-center justify-center gap-2 py-6">
                <Loader2 className="w-5 h-5 animate-spin" /> Connecting…
              </div>
            )}
            {status === "connected" && (
              <>
                <div className={`text-6xl mb-3 ${isSpeaking ? "animate-pulse" : ""}`}>
                  {isSpeaking ? "🔊" : "🎤"}
                </div>
                <div className="text-sm mb-3">
                  {isSpeaking ? `${agent.name} is speaking…` : "Listening — go ahead."}
                </div>
                <Button variant="destructive" className="rounded-none"
                         onClick={endCall} data-testid="va-live-end">
                  <PhoneOff className="w-4 h-4 mr-2" /> End call
                </Button>
              </>
            )}
          </div>
          {error && (
            <div className="text-xs text-red-600 border border-red-500/40 bg-red-50 p-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {error}
            </div>
          )}
          <div className="border border-border max-h-56 overflow-y-auto p-2 space-y-1 bg-muted/10" data-testid="va-live-transcript">
            {transcript.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic text-center py-4">
                Live transcript will appear here…
              </div>
            ) : transcript.map((m, i) => (
              <div key={i} className={`text-xs px-2 py-1 ${m.role === "user" ? "text-right" : ""}`}>
                <span className={`inline-block px-2 py-0.5 ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border border-border"}`}>
                  {m.text}
                </span>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => { endCall(); onClose?.(); }}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// `@elevenlabs/react` v1.10 requires every `useConversation` consumer
// to live under a `<ConversationProvider>`. This wrapper lets the rest
// of the Voice Agents page mount freely without crashing the SDK's
// internal `useRegisterCallbacks` guard.
function LiveCallDialog(props) {
  return (
    <ConversationProvider>
      <LiveCallInner {...props} />
    </ConversationProvider>
  );
}


// ─── Conversations History Dialog ─────────────────────────────────

function ConversationsDialog({ wsId, agent, onClose }) {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/voice-agents/${agent.id}/conversations?limit=50`);
      setRows(data.rows || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openConv = async (r) => {
    setSelected(r); setDetail(null); setLoadingDetail(true);
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/voice-agents/${agent.id}/conversations/${r.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load transcript"));
    } finally { setLoadingDetail(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="va-history">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" /> Call history · {agent.name}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 overflow-hidden">
          <div className="border border-border overflow-y-auto max-h-[60vh]" data-testid="va-history-list">
            {rows === null && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
            {rows && rows.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No calls yet. Start a live call from the agent card to see history here.
              </div>
            )}
            {rows && rows.map((r) => (
              <button
                type="button" key={r.id}
                onClick={() => openConv(r)}
                className={`w-full text-left border-b border-border p-3 hover:bg-muted/30 ${selected?.id === r.id ? "bg-primary/5" : ""}`}
                data-testid={`va-history-row-${r.id}`}
              >
                <div className="text-sm font-medium truncate">
                  {r.call_summary_title || r.id.slice(0, 12)}
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                  <span>{r.status || "—"}</span>
                  {r.duration_seconds != null && <span>· {r.duration_seconds}s</span>}
                  {r.message_count != null && <span>· {r.message_count} msgs</span>}
                </div>
              </button>
            ))}
          </div>
          <div className="border border-border overflow-y-auto max-h-[60vh] p-3" data-testid="va-history-detail">
            {!selected && (
              <div className="text-xs text-muted-foreground italic text-center py-8">
                Select a call to view the transcript.
              </div>
            )}
            {loadingDetail && (
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading transcript…
              </div>
            )}
            {detail && (
              <div className="space-y-2">
                {detail.analysis?.transcript_summary && (
                  <div className="text-xs bg-primary/5 border border-primary/30 p-2">
                    <div className="font-semibold uppercase tracking-widest text-[10px] mb-1">Summary</div>
                    {detail.analysis.transcript_summary}
                  </div>
                )}
                {(detail.transcript || []).map((m, i) => (
                  <div key={i} className={`text-xs px-2 py-1 ${m.role === "user" ? "text-right" : ""}`}>
                    <span className={`inline-block px-2 py-0.5 max-w-[85%] ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-card border border-border"
                    }`}>{m.message || m.text || ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Agent Editor ──────────────────────────────────────────────────

function AgentEditor({ wsId, agent, onClose, onSaved }) {
  const isNew = !agent.id;
  const [form, setForm] = useState({
    name: agent.name || "",
    description: agent.description || "",
    mission: agent.mission || "product_pitch",
    tone: agent.tone || "friendly",
    // Feb 2026 · Default to en-IN so the stored code matches our
    // Indian-first language catalog (older agents stored "en" still
    // round-trip fine via the LanguageSelect fallback).
    language: agent.language || "en-IN",
    persona: agent.persona || "",
    opening_line: agent.opening_line || "",
    script_prompt: agent.script_prompt || "",
    talking_points: (agent.talking_points || []).join("\n"),
    objection_responses: agent.objection_responses || [],
    closing_cta: agent.closing_cta || "",
    knowledge_base_text: agent.knowledge_base_text || "",
    max_call_minutes: agent.max_call_minutes ?? 5,
    is_active: agent.is_active ?? true,
    provider: agent.provider || "elevenlabs",
    voice_id: agent.voice_id || "",
    voice_name: agent.voice_name || "",
    tools_enabled: agent.tools_enabled || {
      send_email: false, send_whatsapp: false,
      create_calendar_event: false, log_note: false,
    },
    // Feb 2026 · Live-call transfer to a human.
    transfer_mode: agent.transfer_mode || "off",
    transfer_phone: agent.transfer_phone || "",
    transfer_phones: agent.transfer_phones || [],
    transfer_triggers: agent.transfer_triggers || [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState([]);
  const [previewBusy, setPreviewBusy] = useState(false);

  /** Detect a non-English Indian language from a voice name/label.
   *  Returns a BCP-47 base code like `hi`, `mr`, `ta`, … or null. */
  const detectVoiceLanguage = (voice) => {
    if (!voice) return null;
    const hay = `${voice.name || ""} ${voice.language || ""}`.toLowerCase();
    const hintMap = {
      hi: /\bhindi\b/, mr: /\bmarathi\b/, ta: /\btamil\b/,
      te: /\btelugu\b/, bn: /\bbengali\b/, gu: /\bgujarati\b/,
      kn: /\bkannada\b/, ml: /\bmalayalam\b/, pa: /\bpunjabi\b/,
    };
    const match = Object.entries(hintMap).find(([, re]) => re.test(hay));
    return match ? match[0] : null;
  };

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/voice-providers/${form.provider}/voices`)
      .then((r) => setVoices(r.data.voices || []))
      .catch(() => setVoices([]));
  }, [wsId, form.provider]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.language) { toast.error("Pick a language"); return; }
    if (!form.opening_line.trim()) {
      toast.error("Opening line is required — it's the first thing the caller hears");
      return;
    }
    if (!form.voice_id) { toast.error("Pick a voice before saving"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        talking_points: form.talking_points.split("\n").map((s) => s.trim()).filter(Boolean),
        transfer_phones: (form.transfer_phones || [])
          .map((p) => (p || "").trim()).filter(Boolean),
        transfer_triggers: (form.transfer_triggers || []).map((t) => {
          const out = { type: t.type };
          if (t.type === "keyword") {
            out.keywords = (t.keywords || [])
              .map((k) => (k || "").trim()).filter(Boolean);
          }
          if (t.type === "after_turns") {
            if (t.turns) out.turns = Number(t.turns);
            if (t.minutes) out.minutes = Number(t.minutes);
          }
          if (t.note) out.note = t.note;
          return out;
        }),
      };
      if (isNew) {
        await api.post(`/workspaces/${wsId}/voice-agents`, payload);
      } else {
        await api.patch(`/workspaces/${wsId}/voice-agents/${agent.id}`, payload);
      }
      toast.success(isNew ? "Agent created" : "Saved");
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  const previewVoice = async () => {
    if (!form.voice_id) { toast.error("Pick a voice first"); return; }
    if (!form.opening_line.trim()) { toast.error("Add an opening line to preview"); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-providers/${form.provider}/tts`,
        { text: form.opening_line, voice_id: form.voice_id });
      const audio = new Audio(data.data_url);
      audio.play();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed — check provider key"));
    } finally { setPreviewBusy(false); }
  };

  const addObjection = () =>
    set("objection_responses", [...(form.objection_responses || []),
                                  { objection: "", response: "" }]);
  const updateObjection = (i, field, v) => {
    const next = [...form.objection_responses];
    next[i] = { ...next[i], [field]: v };
    set("objection_responses", next);
  };
  const removeObjection = (i) =>
    set("objection_responses", form.objection_responses.filter((_, idx) => idx !== i));

  // ─── Feb 2026 · AI Draft Script (Claude Opus 5.5) ───
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCtx, setAiCtx] = useState({
    product_or_offer: "", target_audience: "", special_instructions: "",
  });
  const draftScriptWithAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-agents/generate-script`,
        {
          name: form.name || null,
          description: form.description || null,
          mission: form.mission,
          tone: form.tone,
          language: form.language,
          product_or_offer: aiCtx.product_or_offer || null,
          target_audience: aiCtx.target_audience || null,
          special_instructions: aiCtx.special_instructions || null,
        });
      setForm((f) => ({
        ...f,
        persona: data.persona || f.persona,
        opening_line: data.opening_line || f.opening_line,
        script_prompt: data.script_prompt || f.script_prompt,
        talking_points: (data.talking_points && data.talking_points.length)
          ? data.talking_points.join("\n") : f.talking_points,
        objection_responses: (data.objection_responses && data.objection_responses.length)
          ? data.objection_responses : f.objection_responses,
        closing_cta: data.closing_cta || f.closing_cta,
      }));
      toast.success("Script drafted with AI — review and edit before saving");
      setAiOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "AI drafting failed"));
    } finally { setAiBusy(false); }
  };

  // ─── Transfer helpers ───
  const addTransferTrigger = (type) =>
    set("transfer_triggers", [...(form.transfer_triggers || []), {
      type, keywords: type === "keyword" ? [] : undefined,
      turns: type === "after_turns" ? 6 : undefined,
      minutes: type === "after_turns" ? 3 : undefined,
    }]);
  const updateTransferTrigger = (i, patch) => {
    const next = [...(form.transfer_triggers || [])];
    next[i] = { ...next[i], ...patch };
    set("transfer_triggers", next);
  };
  const removeTransferTrigger = (i) =>
    set("transfer_triggers",
      (form.transfer_triggers || []).filter((_, idx) => idx !== i));
  const addTransferPhone = () =>
    set("transfer_phones", [...(form.transfer_phones || []), ""]);
  const updateTransferPhone = (i, v) => {
    const next = [...(form.transfer_phones || [])];
    next[i] = v;
    set("transfer_phones", next);
  };
  const removeTransferPhone = (i) =>
    set("transfer_phones",
      (form.transfer_phones || []).filter((_, idx) => idx !== i));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="va-editor">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle>{isNew ? "New voice agent" : `Edit · ${agent.name}`}</DialogTitle>
            <Button
              size="sm"
              variant="outline"
              className="rounded-none bg-gradient-to-r from-fuchsia-50 to-indigo-50 border-indigo-400/60 hover:border-indigo-500 text-indigo-700"
              onClick={() => setAiOpen(true)}
              data-testid="va-editor-ai-draft"
              type="button"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              AI draft script
            </Button>
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Name <span className="text-red-500" title="Required to publish">*</span></Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                      className="rounded-none" data-testid="va-editor-name" />
            </div>
            <div>
              <Label className="label-eyebrow">Mission</Label>
              <select value={form.mission} onChange={(e) => set("mission", e.target.value)}
                       className="h-10 w-full text-sm border border-border bg-background px-2 rounded-none"
                       data-testid="va-editor-mission">
                {MISSIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-eyebrow">Tone</Label>
              <select value={form.tone} onChange={(e) => set("tone", e.target.value)}
                       className="h-10 w-full text-sm border border-border bg-background px-2 rounded-none">
                {["friendly", "professional", "urgent", "playful", "curious"].map((t) =>
                  <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-eyebrow">Language <span className="text-red-500" title="Required to publish">*</span></Label>
              <LanguageSelect
                wsId={wsId}
                value={form.language}
                onChange={(code, voiceId) => {
                  set("language", code);
                  // Feb 2026 · When the user picks a language, pre-select
                  // the recommended Eleven voice (unless they've picked
                  // one already) so they get a sensible default per lang.
                  if (voiceId && !form.voice_id) set("voice_id", voiceId);
                }}
                testid="va-editor-language"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Max call (min)</Label>
              <Input type="number" value={form.max_call_minutes} min={1} max={60}
                      onChange={(e) => set("max_call_minutes", Number(e.target.value))}
                      className="rounded-none" />
            </div>
          </div>

          <div>
            <Label className="label-eyebrow">Description (internal)</Label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)}
                    placeholder="e.g. Cold outbound to Food & Bev SMBs"
                    className="rounded-none" data-testid="va-editor-desc" />
          </div>

          <div>
            <Label className="label-eyebrow">Persona (1-2 lines)</Label>
            <Textarea value={form.persona} onChange={(e) => set("persona", e.target.value)}
                       placeholder="You are Priya, a friendly SDR from Acquilix who helps agencies save 5+ hours a week."
                       rows={2} className="rounded-none" data-testid="va-editor-persona" />
          </div>

          <div>
            <Label className="label-eyebrow">Opening line <span className="text-red-500" title="Required to publish — spoken first by the agent">*</span></Label>
            <Input value={form.opening_line} onChange={(e) => set("opening_line", e.target.value)}
                    placeholder="Hi {first_name}, is this a good time for a quick 60 seconds?"
                    className="rounded-none" data-testid="va-editor-opening" />
          </div>

          <div>
            <Label className="label-eyebrow">Talking points (one per line)</Label>
            <Textarea value={form.talking_points}
                       onChange={(e) => set("talking_points", e.target.value)}
                       placeholder={"We help agencies automate 80% of ops\nUsed by 200+ studios\n14-day free trial"}
                       rows={4} className="rounded-none" data-testid="va-editor-talking-points" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="label-eyebrow">Objections + responses</Label>
              <Button size="sm" variant="outline" className="rounded-none h-7"
                       onClick={addObjection}>+ Add</Button>
            </div>
            {(form.objection_responses || []).map((o, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mt-2 items-start">
                <Input value={o.objection || ""} placeholder="Objection"
                        onChange={(e) => updateObjection(i, "objection", e.target.value)}
                        className="rounded-none" />
                <Input value={o.response || ""} placeholder="Suggested response"
                        onChange={(e) => updateObjection(i, "response", e.target.value)}
                        className="rounded-none" />
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={() => removeObjection(i)}>×</Button>
              </div>
            ))}
          </div>

          <div>
            <Label className="label-eyebrow">Closing CTA</Label>
            <Input value={form.closing_cta} onChange={(e) => set("closing_cta", e.target.value)}
                    placeholder="Can I put 15 mins on your calendar this week?"
                    className="rounded-none" data-testid="va-editor-cta" />
          </div>

          <div>
            <Label className="label-eyebrow">Knowledge base (plain text)</Label>
            <Textarea value={form.knowledge_base_text}
                       onChange={(e) => set("knowledge_base_text", e.target.value)}
                       placeholder="Product features, pricing, competitors, common Q&A…"
                       rows={5} className="rounded-none" data-testid="va-editor-kb" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Max ~8000 chars. Pasted here is baked into every call; use the ingested sources below for larger / changing material.
            </div>
          </div>

          {/* Feb 2026 · Per-agent RAG knowledge (pasted text + URL fetch).
              Only enabled once the agent has been saved at least once —
              we need its stable id to attach chunks. */}
          {agent.id ? (
            <div className="border border-border p-3 space-y-2">
              <Label className="label-eyebrow">Supporting material (RAG)</Label>
              <AgentKnowledgeSection wsId={wsId} agentId={agent.id} />
            </div>
          ) : (
            <div className="border border-dashed border-border p-3 text-[11px] text-muted-foreground italic">
              Save the agent once to enable supporting-material uploads (RAG index attaches to the saved agent).
            </div>
          )}

          <div className="border border-border p-3 space-y-2">
            <Label className="label-eyebrow">🎙 Voice ({form.provider}) <span className="text-red-500" title="Required to publish">*</span></Label>
            {voices.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">
                No voices available. Configure the {form.provider} API key in the Providers tab to load voices.
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <select value={form.voice_id}
                         onChange={(e) => {
                           const v = voices.find((x) => x.id === e.target.value);
                           set("voice_id", e.target.value);
                           set("voice_name", v?.name || "");
                           // Auto-sync language from the picked voice —
                           // keeps user from accidentally publishing a
                           // Hindi voice with English language.
                           const detected = detectVoiceLanguage(v);
                           if (detected) {
                             const current = (form.language || "").toLowerCase();
                             if (current !== detected && !current.startsWith(detected + "-")) {
                               set("language", detected);
                               toast.info(`Language set to ${detected.toUpperCase()} to match the voice`);
                             }
                           }
                         }}
                         className="h-9 flex-1 text-xs border border-border bg-background px-2 rounded-none"
                         data-testid="va-editor-voice">
                  <option value="">— Pick a voice —</option>
                  {voices.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}{v.gender ? ` · ${v.gender}` : ""}{v.language ? ` · ${v.language}` : ""}
                    </option>
                  ))}
                </select>
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={previewVoice} disabled={previewBusy || !form.voice_id}
                         data-testid="va-editor-preview">
                  {previewBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Play className="w-3 h-3 mr-1" /> Preview</>}
                </Button>
              </div>
            )}
            {(() => {
              /* Feb 2026 · Fallback mismatch warning for a voice we
                 can't auto-detect (user manually changed the language
                 field after picking a voice). Normally the voice-pick
                 handler auto-syncs language so this banner stays
                 hidden. */
              const v = voices.find((x) => x.id === form.voice_id);
              const code = detectVoiceLanguage(v);
              if (!code) return null;
              const lang = (form.language || "").toLowerCase();
              if (lang === code || lang.startsWith(code + "-")) return null;
              return (
                <div className="flex items-center gap-2 text-[11px] bg-amber-50 border border-amber-300 px-2 py-1.5"
                     data-testid="va-editor-lang-mismatch">
                  <span className="text-amber-800">
                    This voice sounds like <b>{code.toUpperCase()}</b> but the agent language is <b>{form.language || "en"}</b>.
                    Publishing will fail — switch the Language field to match.
                  </span>
                  <button
                    type="button"
                    onClick={() => set("language", code)}
                    className="ml-auto text-amber-900 underline hover:no-underline">
                    Set language to {code}
                  </button>
                </div>
              );
            })()}
          </div>

          <div className="border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="va-editor-tools">
            <div className="label-eyebrow text-[10px] text-primary flex items-center gap-2">
              <Wand2 className="w-3 h-3" /> Mid-call actions
            </div>
            <div className="text-[11px] text-muted-foreground">
              When enabled, the AI agent can perform these actions during a live call.
              It will only invoke them when the caller explicitly agrees.
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["send_email", "Send follow-up email", "Agent asks for the caller's email and sends a follow-up."],
                ["send_whatsapp", "Send WhatsApp message", "Sends via the workspace's Interakt integration."],
                ["create_calendar_event", "Book a calendar event", "Agent schedules a meeting."],
                ["log_note", "Log CRM note", "Persists an important note from the call."],
              ].map(([key, label, hint]) => (
                <label key={key}
                        className={`flex items-start gap-2 p-2 border cursor-pointer ${
                          form.tools_enabled?.[key]
                            ? "border-primary bg-primary/10"
                            : "border-border hover:bg-muted/40"
                        }`}
                        data-testid={`va-tool-${key}`}>
                  <input type="checkbox"
                          checked={!!form.tools_enabled?.[key]}
                          onChange={(e) => set("tools_enabled",
                            { ...form.tools_enabled, [key]: e.target.checked })}
                          className="mt-0.5" />
                  <span className="text-xs">
                    <span className="font-semibold block">{label}</span>
                    <span className="text-muted-foreground text-[10px]">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="border border-amber-500/30 bg-amber-50/40 p-3 space-y-3" data-testid="va-editor-transfer">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow text-[10px] text-amber-800 flex items-center gap-2">
                <PhoneForwarded className="w-3 h-3" /> Live-call transfer to human
              </div>
              <select
                value={form.transfer_mode}
                onChange={(e) => set("transfer_mode", e.target.value)}
                className="h-8 text-xs border border-border bg-background px-2 rounded-none"
                data-testid="va-editor-transfer-mode"
              >
                <option value="off">Off — never transfer</option>
                <option value="single">Single number</option>
                <option value="round_robin">Round-robin pool</option>
              </select>
            </div>
            <div className="text-[11px] text-muted-foreground">
              When any trigger below fires mid-call, the AI warm-transfers to a human.
            </div>
            {form.transfer_mode === "single" && (
              <Input
                value={form.transfer_phone || ""}
                onChange={(e) => set("transfer_phone", e.target.value)}
                placeholder="+919812345678"
                className="rounded-none h-9"
                data-testid="va-editor-transfer-phone"
              />
            )}
            {form.transfer_mode === "round_robin" && (
              <div className="space-y-2">
                {(form.transfer_phones || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={p || ""}
                      placeholder="+919812345678"
                      onChange={(e) => updateTransferPhone(i, e.target.value)}
                      className="rounded-none h-9"
                      data-testid={`va-editor-transfer-phone-${i}`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                      onClick={() => removeTransferPhone(i)}
                      data-testid={`va-editor-remove-transfer-phone-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-none h-8"
                  onClick={addTransferPhone}
                  data-testid="va-editor-add-transfer-phone"
                >
                  + Add agent number
                </Button>
              </div>
            )}
            {form.transfer_mode !== "off" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow text-[10px] text-amber-800">
                    Triggers
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      ["hot_lead", "Hot lead"],
                      ["explicit_request", "Explicit request"],
                      ["after_turns", "After turns / minutes"],
                      ["keyword", "Keyword"],
                      ["objection", "Objection / complaint"],
                    ].map(([type, label]) => (
                      <Button
                        key={type}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-none h-7 text-[10px]"
                        onClick={() => addTransferTrigger(type)}
                        data-testid={`va-editor-add-trigger-${type}`}
                      >
                        + {label}
                      </Button>
                    ))}
                  </div>
                </div>
                {(form.transfer_triggers || []).length === 0 && (
                  <div className="text-[11px] italic text-muted-foreground">
                    No triggers added yet — the agent will never transfer.
                  </div>
                )}
                {(form.transfer_triggers || []).map((t, i) => (
                  <div
                    key={i}
                    className="border border-border bg-background p-2 flex items-start gap-2"
                    data-testid={`va-editor-trigger-${i}`}
                  >
                    <Badge variant="outline" className="rounded-none text-[10px]">
                      {t.type.replace(/_/g, " ")}
                    </Badge>
                    <div className="flex-1 space-y-1">
                      {t.type === "keyword" && (
                        <Input
                          value={(t.keywords || []).join(", ")}
                          onChange={(e) => updateTransferTrigger(i, {
                            keywords: e.target.value.split(",")
                              .map((s) => s.trim()).filter(Boolean),
                          })}
                          placeholder="speak to manager, cancel, refund"
                          className="rounded-none h-8 text-xs"
                          data-testid={`va-editor-trigger-keywords-${i}`}
                        />
                      )}
                      {t.type === "after_turns" && (
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            min={1}
                            value={t.turns ?? ""}
                            onChange={(e) => updateTransferTrigger(i, {
                              turns: Number(e.target.value) || null,
                            })}
                            placeholder="Turns (e.g. 6)"
                            className="rounded-none h-8 text-xs"
                            data-testid={`va-editor-trigger-turns-${i}`}
                          />
                          <Input
                            type="number"
                            min={1}
                            value={t.minutes ?? ""}
                            onChange={(e) => updateTransferTrigger(i, {
                              minutes: Number(e.target.value) || null,
                            })}
                            placeholder="Minutes (e.g. 3)"
                            className="rounded-none h-8 text-xs"
                            data-testid={`va-editor-trigger-minutes-${i}`}
                          />
                        </div>
                      )}
                      <Input
                        value={t.note || ""}
                        onChange={(e) => updateTransferTrigger(i, { note: e.target.value })}
                        placeholder="Note (optional) — shown to the LLM as rationale"
                        className="rounded-none h-7 text-[11px]"
                        data-testid={`va-editor-trigger-note-${i}`}
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                      onClick={() => removeTransferTrigger(i)}
                      data-testid={`va-editor-remove-trigger-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
        {aiOpen && (
          <Dialog open onOpenChange={(o) => !o && !aiBusy && setAiOpen(false)}>
            <DialogContent className="rounded-none max-w-md" data-testid="va-ai-draft-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" /> AI draft script
                </DialogTitle>
                <div className="text-xs text-muted-foreground">
                  Claude Opus 5.5 drafts persona, opening line, talking points, objection handling and CTA. Review before saving.
                </div>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-eyebrow">Product / offer *</Label>
                  <Textarea
                    rows={3}
                    value={aiCtx.product_or_offer}
                    onChange={(e) => setAiCtx((c) => ({ ...c, product_or_offer: e.target.value }))}
                    placeholder="One-liner about what you sell + the hook (price, bonus, timing)."
                    className="rounded-none"
                    data-testid="va-ai-product"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Target audience</Label>
                  <Input
                    value={aiCtx.target_audience}
                    onChange={(e) => setAiCtx((c) => ({ ...c, target_audience: e.target.value }))}
                    placeholder="e.g. SMB founders, 10-50 staff, SaaS vertical"
                    className="rounded-none"
                    data-testid="va-ai-audience"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Special instructions</Label>
                  <Textarea
                    rows={2}
                    value={aiCtx.special_instructions}
                    onChange={(e) => setAiCtx((c) => ({ ...c, special_instructions: e.target.value }))}
                    placeholder="e.g. avoid price, push demo signup, mention 14-day trial"
                    className="rounded-none"
                    data-testid="va-ai-instructions"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-none"
                  onClick={() => setAiOpen(false)}
                  disabled={aiBusy}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="rounded-none"
                  onClick={draftScriptWithAI}
                  disabled={aiBusy || !aiCtx.product_or_offer.trim()}
                  data-testid="va-ai-draft-go"
                >
                  {aiBusy
                    ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Drafting…</>
                    : <><Sparkles className="w-3.5 h-3.5 mr-2" /> Draft with AI</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving}
                   data-testid="va-editor-save">
            <Save className="w-3.5 h-3.5 mr-2" />
            {saving ? "Saving…" : (isNew ? "Create" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Chat Dry-Run Simulator ────────────────────────────────────────

function SimulatorDialog({ wsId, agent, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input.trim() };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/voice-agents/${agent.id}/simulate`,
        { messages: newMsgs, user_input: userMsg.content });
      setMessages([...newMsgs, { role: "assistant", content: data.agent_reply }]);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Simulation failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[85vh] flex flex-col" data-testid="va-simulator">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" /> Dry-run · {agent.name}
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Chat-based simulation using Claude Sonnet 4.5. No phone call happens. Type as the prospect would speak.
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-[300px] max-h-[60vh] overflow-y-auto border border-border p-3 space-y-2 bg-muted/10">
          {messages.length === 0 && (
            <div className="text-xs text-muted-foreground italic text-center py-8">
              Send the first message as if you were the prospect answering the call.
              The agent will follow its script and knowledge base.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                 data-testid={`va-msg-${m.role}-${i}`}>
              <div className={`max-w-[75%] px-3 py-2 text-sm ${
                m.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-card border border-border"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-card border border-border px-3 py-2 text-sm text-muted-foreground italic">
                <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> {agent.name} is thinking…
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 border-t border-border pt-3">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type as the prospect… (Enter to send)"
                  className="rounded-none" disabled={busy}
                  data-testid="va-sim-input" />
          <Button className="rounded-none" onClick={send} disabled={busy || !input.trim()}
                   data-testid="va-sim-send">
            Send
          </Button>
          <Button variant="outline" className="rounded-none"
                   onClick={() => setMessages([])} disabled={busy || messages.length === 0}
                   data-testid="va-sim-reset">
            Reset
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Providers Tab ────────────────────────────────────────────────

function ProvidersTab({ wsId }) {
  const [rows, setRows] = useState(null);
  const [keyDialog, setKeyDialog] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [phoneCfg, setPhoneCfg] = useState(null);  // ElevenLabs carrier config
  const load = () => Promise.all([
    api.get(`/workspaces/${wsId}/voice-providers`),
    api.get(`/workspaces/${wsId}/voice-agent-carrier-config`).catch(() => ({ data: {} })),
  ]).then(([r, c]) => {
    setRows(r.data.providers || []);
    setPhoneCfg(c.data || {});
  }).catch(() => setRows([]));
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);

  // Feb 2026 · deep-link from the Voice Campaigns readiness banner.
  // `?setup=elevenlabs` auto-opens the ElevenLabs setup dialog so the
  // user doesn't have to hunt for the Change-key button on mobile.
  useEffect(() => {
    if (!rows) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("setup") === "elevenlabs") {
      const el = rows.find((p) => p.slug === "elevenlabs");
      if (el) setKeyDialog(el);
      sp.delete("setup");
      const q = sp.toString();
      window.history.replaceState(null, "",
        window.location.pathname + (q ? `?${q}` : ""));
    }
  }, [rows]);

  const testKey = async (p) => {
    try {
      setTestResults((t) => ({ ...t, [p.slug]: { busy: true } }));
      const { data } = await api.post(`/workspaces/${wsId}/voice-providers/${p.slug}/test`);
      setTestResults((t) => ({ ...t, [p.slug]: data }));
    } catch (e) {
      setTestResults((t) => ({ ...t, [p.slug]: { ok: false,
        message: e?.response?.data?.detail || "Test failed" } }));
    }
  };

  const removeKey = async (p) => {
    if (!window.confirm(`Remove ${p.label} key from this workspace?`)) return;
    await api.delete(`/workspaces/${wsId}/voice-providers/${p.slug}`);
    toast.success("Key removed");
    load();
  };

  if (rows === null) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        API keys are stored encrypted at rest. Every key is workspace-specific.
      </div>
      {rows.map((p) => {
        const test = testResults[p.slug];
        return (
          <Card key={p.slug} className="rounded-none" data-testid={`va-provider-${p.slug}`}>
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold flex items-center gap-2">
                  {p.label}
                  {p.configured
                    ? <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-500/40">✓ Configured</Badge>
                    : <Badge variant="outline" className="text-[9px]">Not set</Badge>}
                  {p.slug === "elevenlabs" && p.configured
                    && !phoneCfg?.elevenlabs_phone_number_id && (
                    <Badge className="text-[9px] bg-amber-100 text-amber-900 border border-amber-500/40"
                            data-testid="va-provider-phone-id-missing">
                      ⚠ Phone Number ID missing
                    </Badge>
                  )}
                  {p.slug === "elevenlabs" && phoneCfg?.elevenlabs_phone_number_id && (
                    <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-500/40">
                      ☎ {phoneCfg.elevenlabs_phone_number_id.slice(0, 12)}…
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Capabilities: {p.capabilities.join(", ")}
                </div>
                {p.updated_at && (
                  <div className="text-[10px] text-muted-foreground">
                    Updated {p.updated_at?.slice(0, 16).replace("T", " ")}
                    {p.updated_by_name && ` by ${p.updated_by_name}`}
                  </div>
                )}
                {test && (
                  <div className={`text-[11px] mt-1 flex items-center gap-1 ${test.ok ? "text-emerald-700" : "text-red-600"}`}>
                    {test.busy ? <Loader2 className="w-3 h-3 animate-spin" />
                     : test.ok ? <CheckCircle2 className="w-3 h-3" />
                     : <AlertCircle className="w-3 h-3" />}
                    <span>{test.message || (test.busy ? "Testing…" : "")}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="rounded-none"
                         onClick={() => setKeyDialog(p)}
                         data-testid={`va-provider-set-${p.slug}`}>
                  <KeyRound className="w-3 h-3 mr-1" /> {p.configured ? "Change" : "Set"} key
                </Button>
                {p.configured && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-none"
                             onClick={() => testKey(p)}
                             data-testid={`va-provider-test-${p.slug}`}>
                      Test
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none text-red-600"
                             onClick={() => removeKey(p)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
      {keyDialog && (
        <ProviderKeyDialog wsId={wsId} provider={keyDialog}
                            onClose={() => setKeyDialog(null)}
                            onSaved={() => { setKeyDialog(null); load(); }} />
      )}
      <VoiceCostConfigCard wsId={wsId} />
    </div>
  );
}

/** Workspace-level config for voice campaign cost tracking + the
 *  7-day performance score weighting. Lives on the Providers tab
 *  because it's a once-and-forget setup that sits next to the
 *  provider keys that drive the actual spend. */
function VoiceCostConfigCard({ wsId }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/workspaces/${wsId}/voice-agent-cost-config`)
    .then((r) => { setCfg(r.data); setDraft(r.data); })
    .catch(() => {});
  useEffect(() => { if (wsId) load(); /* eslint-disable-next-line */ }, [wsId]);
  if (!cfg || !draft) return null;
  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const normaliseWeights = () => {
    const b = Math.max(0, Math.min(1, Number(draft.score_weight_booked) || 0));
    const c = Math.max(0, Math.min(1, Number(draft.score_weight_connect) || 0));
    const sum = b + c;
    if (sum === 0) return { b: 0.6, c: 0.4 };
    return { b: b / sum, c: c / sum };
  };
  const normalisePresets = () => {
    const raw = typeof draft.budget_cap_presets === "string"
      ? draft.budget_cap_presets.split(/[,\s]+/)
      : (draft.budget_cap_presets || []);
    const nums = Array.from(new Set(raw.map(Number).filter((n) => n > 0)))
      .sort((a, b) => a - b).slice(0, 6);
    return nums;
  };
  const save = async () => {
    setSaving(true);
    try {
      const { b, c } = normaliseWeights();
      const { data } = await api.put(
        `/workspaces/${wsId}/voice-agent-cost-config`,
        {
          cost_per_minute: Number(draft.cost_per_minute),
          currency: draft.currency,
          score_weight_booked: b,
          score_weight_connect: c,
          budget_cap_presets: normalisePresets(),
        });
      setCfg(data); setDraft(data);
      toast.success("Cost + score settings saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };
  return (
    <Card className="rounded-none" data-testid="va-cost-config-card">
      <CardContent className="p-4 space-y-3">
        <div className="font-semibold flex items-center gap-2">
          <span>Voice cost + scoring</span>
          <Badge variant="outline" className="text-[10px]">Workspace-wide</Badge>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Each voice campaign dashboard multiplies total call minutes by the
          per-minute rate below. The 7-day score on each agent card uses the
          weights below. Weights are re-normalised to sum to 1 on save.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Cost per minute</Label>
            <div className="flex items-center gap-2">
              <Input value={draft.currency || "INR"}
                      onChange={(e) => set("currency", e.target.value.toUpperCase().slice(0, 8))}
                      className="rounded-none w-20 uppercase"
                      data-testid="va-cost-currency" />
              <Input type="number" step="0.1" min="0"
                      value={draft.cost_per_minute}
                      onChange={(e) => set("cost_per_minute", e.target.value)}
                      className="rounded-none flex-1"
                      data-testid="va-cost-per-minute" />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Default ₹8/min · update to match your ElevenLabs plan.
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">7-day score weights</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className="text-[10px] text-muted-foreground mb-1">Booked</div>
                <Input type="number" step="0.1" min="0" max="1"
                        value={draft.score_weight_booked}
                        onChange={(e) => set("score_weight_booked", e.target.value)}
                        className="rounded-none"
                        data-testid="va-weight-booked" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] text-muted-foreground mb-1">Connect</div>
                <Input type="number" step="0.1" min="0" max="1"
                        value={draft.score_weight_connect}
                        onChange={(e) => set("score_weight_connect", e.target.value)}
                        className="rounded-none"
                        data-testid="va-weight-connect" />
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Both numbers are relative — if you enter 0.6 / 0.4 the score weights 60% booked, 40% connect.
            </div>
          </div>
        </div>
        <div className="border-t pt-3">
          <Label className="label-eyebrow">Budget cap presets</Label>
          <Input value={Array.isArray(draft.budget_cap_presets)
                           ? draft.budget_cap_presets.join(", ")
                           : (draft.budget_cap_presets || "")}
                   onChange={(e) => setDraft((d) => ({
                     ...d, budget_cap_presets: e.target.value }))}
                   placeholder="100, 500, 2000"
                   className="rounded-none"
                   data-testid="va-cap-presets" />
          <div className="text-[10px] text-muted-foreground mt-1">
            Comma-separated amounts in your workspace currency. Shown as one-tap chips on the campaign create dialog. Up to 6; duplicates & non-positive values are dropped on save.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          {dirty && (
            <Button size="sm" variant="outline" className="rounded-none"
                     onClick={() => setDraft(cfg)}>
              Discard
            </Button>
          )}
          <Button size="sm" className="rounded-none" onClick={save}
                   disabled={!dirty || saving}
                   data-testid="va-cost-save">
            <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProviderKeyDialog({ wsId, provider, onClose, onSaved }) {
  const [key, setKey] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [saving, setSaving] = useState(false);
  const isEleven = provider.slug === "elevenlabs";
  // Feb 2026 — load the saved phone_number_id so the user can tell at a
  // glance whether outbound calls will have a carrier to leave from.
  useEffect(() => {
    if (!isEleven) return;
    api.get(`/workspaces/${wsId}/voice-agent-carrier-config`)
      .then((r) => setPhoneId(r.data?.elevenlabs_phone_number_id || ""))
      .catch(() => {});
  }, [wsId, isEleven]);
  const save = async () => {
    const trimmedKey = key.trim();
    const trimmedPhoneId = phoneId.trim();
    if (trimmedKey && trimmedKey.length < 8) {
      toast.error("Key looks too short"); return;
    }
    if (!trimmedKey && !isEleven) {
      toast.error("API key is required"); return;
    }
    if (!trimmedKey && !trimmedPhoneId) {
      toast.error("Enter the API key or the Phone Number ID"); return;
    }
    setSaving(true);
    try {
      if (trimmedKey) {
        await api.put(`/workspaces/${wsId}/voice-providers/${provider.slug}`,
                       { api_key: trimmedKey });
      }
      if (isEleven) {
        await api.put(`/workspaces/${wsId}/voice-agent-carrier-config`,
                       { elevenlabs_phone_number_id: trimmedPhoneId || null });
      }
      toast.success(`${provider.label} saved`);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none">
        <DialogHeader>
          <DialogTitle>{provider.label} setup</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>API Key {provider.configured && <span className="text-[10px] text-muted-foreground">(leave blank to keep current)</span>}</Label>
            <Input type="password" value={key} onChange={(e) => setKey(e.target.value)}
                    placeholder={provider.configured ? "•••• keep current ••••" : "Paste key…"}
                    className="rounded-none"
                    data-testid={`va-provider-key-input-${provider.slug}`}
                    autoFocus={!provider.configured}
                    onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
            {provider.key_help_url && (
              <a href={provider.key_help_url} target="_blank" rel="noreferrer"
                 className="text-[11px] text-primary underline mt-1 inline-block">
                Get your key →
              </a>
            )}
          </div>
          {isEleven && (
            <div className="border-t pt-3">
              <Label>
                Phone Number ID <span className="text-red-500" title="Required for outbound voice campaigns">*</span>
              </Label>
              <Input value={phoneId} onChange={(e) => setPhoneId(e.target.value)}
                      placeholder="phnum_xxxxxxxxxxxxxxx"
                      className="rounded-none"
                      data-testid="va-provider-phone-id-input"
                      autoFocus={provider.configured}
                      onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
              <a href="https://elevenlabs.io/app/conversational-ai/phone-numbers"
                 target="_blank" rel="noreferrer"
                 className="text-[11px] text-primary underline mt-1 inline-block">
                Find your Phone Number ID in ElevenLabs → Conversational AI → Phone numbers →
              </a>
              <div className="text-[10px] text-muted-foreground mt-1">
                Required for Voice Campaigns to actually dial out. Without it, every call fails with
                <code className="mx-1">elevenlabs_phone_number_id_missing</code>.
              </div>
            </div>
          )}
          <div className="text-[10px] text-muted-foreground pt-1">
            Stored encrypted at rest. Only this workspace can access it.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving}
                   data-testid={`va-provider-key-save-${provider.slug}`}>
            <Save className="w-3.5 h-3.5 mr-2" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Feb 2026 · Common Training tab + per-agent Knowledge section ───

/**
 * CommonKbTab — workspace-wide training inherited by every agent.
 * One text block + optional talking points + objection responses.
 * Save is a single PUT so the whole doc is atomic.
 */
function CommonKbTab({ wsId }) {
  const [form, setForm] = useState({ text: "", talking_points: [], objection_responses: [], is_active: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tpInput, setTpInput] = useState("");
  const [objKey, setObjKey] = useState("");
  const [objVal, setObjVal] = useState("");
  // Feb 2026 fix · One-shot hydration. Prevents double-fetch races
  // from overwriting typed text.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!wsId) return;
    // Feb 2026 fix · The strict-mode / nav remount double-fires this
    // effect, so a late response from the first call stomped on text
    // the user typed between the two renders. `ignore` flag discards
    // any response that lands after the effect has been superseded,
    // and the hydration itself only runs once (the ref guard).
    let ignore = false;
    api.get(`/workspaces/${wsId}/voice-agent-common-kb`)
      .then(({ data }) => {
        if (ignore || hydratedRef.current) return;
        hydratedRef.current = true;
        setForm({
          text: data?.text || "",
          talking_points: data?.talking_points || [],
          objection_responses: data?.objection_responses || [],
          is_active: data?.is_active !== false,
        });
      })
      .catch(() => {})
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [wsId]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/voice-agent-common-kb`, form);
      toast.success("Common training saved — every agent will pick it up on the next turn");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save common training"));
    } finally { setSaving(false); }
  };
  if (loading) return <div className="text-sm text-muted-foreground">Loading common training…</div>;
  return (
    <div className="space-y-5" data-testid="va-common-kb">
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-semibold text-sm">Workspace-wide training</h3>
              <p className="text-xs text-muted-foreground">
                These instructions are prepended to every agent's system prompt. Great for pricing, SLAs, standard offers and tone. Each agent can still add its own extras.
              </p>
            </div>
            <label className="text-xs flex items-center gap-2 shrink-0">
              <input type="checkbox" checked={form.is_active}
                      onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                      data-testid="va-common-kb-active" />
              Active
            </label>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Common knowledge / guidance</Label>
            <Textarea rows={8} value={form.text}
                       onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                       className="rounded-none text-xs"
                       placeholder="e.g. We are Acquilix, a creative-ops platform for Indian agencies. Our standard SLA is 24 hours. Working hours are 10 AM to 7 PM IST. Never promise a feature we have not shipped."
                       data-testid="va-common-kb-text" />
            <div className="text-[10px] text-muted-foreground mt-1">{form.text.length}/20,000 characters</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Common talking points</Label>
            <div className="flex gap-2 mt-1">
              <Input value={tpInput} onChange={(e) => setTpInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && tpInput.trim()) { e.preventDefault();
                        setForm((f) => ({ ...f, talking_points: [...f.talking_points, tpInput.trim()] }));
                        setTpInput(""); } }}
                      className="h-8 rounded-none text-xs"
                      placeholder="Add a talking point and press Enter"
                      data-testid="va-common-kb-tp-input" />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {form.talking_points.map((tp, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-muted/40 border border-border px-2 py-0.5">
                  {tp}
                  <button type="button" onClick={() =>
                      setForm((f) => ({ ...f, talking_points: f.talking_points.filter((_, j) => j !== i) }))}
                           className="text-muted-foreground hover:text-foreground"
                           aria-label="Remove talking point">×</button>
                </span>
              ))}
              {form.talking_points.length === 0 && (
                <span className="text-[11px] italic text-muted-foreground">No talking points added yet.</span>
              )}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Common objection to response</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
              <Input value={objKey} onChange={(e) => setObjKey(e.target.value)}
                      className="h-8 rounded-none text-xs"
                      placeholder="If they say..."
                      data-testid="va-common-kb-obj-key" />
              <div className="flex gap-2">
                <Input value={objVal} onChange={(e) => setObjVal(e.target.value)}
                        className="h-8 rounded-none text-xs flex-1"
                        placeholder="Reply with..."
                        data-testid="va-common-kb-obj-val" />
                <Button size="sm" variant="outline" className="rounded-none h-8"
                          onClick={() => {
                            if (!objKey.trim() || !objVal.trim()) return;
                            setForm((f) => ({ ...f, objection_responses: [...f.objection_responses, { objection: objKey.trim(), response: objVal.trim() }] }));
                            setObjKey(""); setObjVal("");
                          }}
                          data-testid="va-common-kb-obj-add">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {form.objection_responses.map((o, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] border border-border bg-muted/20 px-2 py-1">
                  <div className="flex-1">
                    <div className="font-semibold">If: {o.objection}</div>
                    <div className="text-muted-foreground">Reply: {o.response}</div>
                  </div>
                  <button type="button" onClick={() =>
                        setForm((f) => ({ ...f, objection_responses: f.objection_responses.filter((_, j) => j !== i) }))}
                           className="text-muted-foreground hover:text-foreground">×</button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <Button onClick={save} disabled={saving} className="rounded-none"
                     data-testid="va-common-kb-save">
              {saving ? (<><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Saving...</>) : (<><Save className="w-3.5 h-3.5 mr-1" />Save common training</>)}
            </Button>
            <span className="text-[11px] text-muted-foreground">Changes apply to all agents on their next turn.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * AgentKnowledgeSection — per-agent RAG uploader. Embedded in the agent
 * edit drawer. Supports pasted text + URL ingestion. File upload is
 * out-of-scope for v1 and goes to the backlog (needs object-storage
 * + PDF text extraction).
 */
export function AgentKnowledgeSection({ wsId, agentId }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [mode, setMode] = useState("text");
  const [label, setLabel] = useState("");
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  // Feb 2026 · File-upload path. Uses the existing Emergent object
  // storage endpoint (/upload-url + S3-style PUT). We hand the final
  // file URL to the backend which fetches + extracts text.
  const [fileLabel, setFileLabel] = useState("");
  const [filePicked, setFilePicked] = useState(null);
  const fileInputRef = useRef(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${wsId}/voice-agents/${agentId}/knowledge`);
      setSources(data?.sources || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (wsId && agentId) load(); /* eslint-disable-next-line */ }, [wsId, agentId]);
  const ingest = async () => {
    const trimmedLabel = (mode === "file" ? fileLabel : label).trim();
    if (!trimmedLabel) { toast.error("Give the source a label"); return; }
    if (mode === "text" && !content.trim()) { toast.error("Paste some text to ingest"); return; }
    if (mode === "url" && !url.trim()) { toast.error("Paste a URL"); return; }
    if (mode === "file" && !filePicked) { toast.error("Pick a file first"); return; }
    setIngesting(true);
    try {
      const body = { source_type: mode, source_label: trimmedLabel };
      if (mode === "text") body.content = content;
      if (mode === "url") body.url = url;
      if (mode === "file") {
        // Feb 2026 · Two-step upload via Emergent Object Storage. We
        // ask the backend for a signed URL, PUT the file bytes, then
        // call ingest with the public read URL — the backend fetches +
        // extracts text server-side (pypdf for PDFs, utf-8 for text).
        const { data: up } = await api.post(
          `/workspaces/${wsId}/attachments/upload-url`,
          { filename: filePicked.name, content_type: filePicked.type });
        await fetch(up.upload_url, { method: "PUT", body: filePicked,
                                       headers: { "Content-Type": filePicked.type } });
        body.file_url = up.file_url;
      }
      const { data } = await api.post(`/workspaces/${wsId}/voice-agents/${agentId}/knowledge`, body);
      const vec = data.embedded ? " · embedded" : "";
      toast.success(`Ingested ${data.chunks_added} chunk${data.chunks_added === 1 ? "" : "s"}${vec}`);
      setLabel(""); setContent(""); setUrl("");
      setFileLabel(""); setFilePicked(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not ingest"));
    } finally { setIngesting(false); }
  };
  const del = async (sourceId) => {
    if (!window.confirm("Remove this source and all its chunks?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/voice-agents/${agentId}/knowledge/${sourceId}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="space-y-3" data-testid="va-agent-knowledge">
      <div>
        <Label className="text-[10px] uppercase tracking-widest">Add supporting material</Label>
        <div className="flex gap-2 mb-2 mt-1">
          <button type="button" onClick={() => setMode("text")}
                   className={`text-[11px] px-2 py-0.5 border rounded-none ${mode === "text" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                   data-testid="va-knowledge-mode-text">Paste text</button>
          <button type="button" onClick={() => setMode("url")}
                   className={`text-[11px] px-2 py-0.5 border rounded-none ${mode === "url" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                   data-testid="va-knowledge-mode-url">Fetch URL</button>
          <button type="button" onClick={() => setMode("file")}
                   className={`text-[11px] px-2 py-0.5 border rounded-none ${mode === "file" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                   data-testid="va-knowledge-mode-file">Upload file</button>
        </div>
        {mode !== "file" ? (
          <Input value={label} onChange={(e) => setLabel(e.target.value)}
                  className="h-8 rounded-none text-xs mb-2"
                  placeholder="Label (e.g. Pricing one-pager, Case study - Acme)"
                  data-testid="va-knowledge-label" />
        ) : null}
        {mode === "text" && (
          <Textarea rows={5} value={content} onChange={(e) => setContent(e.target.value)}
                     className="rounded-none text-xs"
                     placeholder="Paste the content. We chunk it ~900 characters per piece and retrieve the best matches at call time."
                     data-testid="va-knowledge-text" />
        )}
        {mode === "url" && (
          <Input value={url} onChange={(e) => setUrl(e.target.value)}
                  className="h-8 rounded-none text-xs"
                  placeholder="https://example.com/pricing"
                  data-testid="va-knowledge-url" />
        )}
        {mode === "file" && (
          <div className="space-y-2">
            <Input value={fileLabel} onChange={(e) => setFileLabel(e.target.value)}
                    className="h-8 rounded-none text-xs"
                    placeholder="Label (e.g. Product one-pager PDF)"
                    data-testid="va-knowledge-file-label" />
            <input ref={fileInputRef} type="file" accept=".pdf,.txt,.md,.csv"
                     onChange={(e) => setFilePicked(e.target.files?.[0] || null)}
                     className="text-xs"
                     data-testid="va-knowledge-file-input" />
            {filePicked && (
              <div className="text-[10px] text-muted-foreground">
                {filePicked.name} · {(filePicked.size / 1024).toFixed(1)} KB
              </div>
            )}
            <div className="text-[10px] text-muted-foreground">PDF, TXT, MD or CSV · max 10 MB</div>
          </div>
        )}
        <Button size="sm" className="rounded-none mt-2" onClick={ingest} disabled={ingesting}
                 data-testid="va-knowledge-ingest">
          {ingesting ? (<><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Ingesting...</>) : (<><Plus className="w-3.5 h-3.5 mr-1" />Ingest</>)}
        </Button>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest">Ingested sources</Label>
        {loading ? (
          <div className="text-xs text-muted-foreground mt-1">Loading...</div>
        ) : sources.length === 0 ? (
          <div className="text-xs text-muted-foreground italic mt-1">No supporting material yet.</div>
        ) : (
          <div className="mt-1 border border-border divide-y divide-border">
            {sources.map((s) => (
              <div key={s.source_id} className="px-2 py-1.5 flex items-center gap-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{s.source_label}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {s.source_type.toUpperCase()} · {s.chunk_count} chunk{s.chunk_count === 1 ? "" : "s"}
                    {s.url ? ` · ${s.url}` : ""}
                  </div>
                </div>
                <button type="button" onClick={() => del(s.source_id)}
                         className="text-muted-foreground hover:text-rose-600"
                         data-testid={`va-knowledge-del-${s.source_id}`}
                         title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * LanguageSelect — fetches the workspace's supported voice languages
 * (Indian-first catalog maintained server-side) and lets the user pick
 * one. Calls `onChange(code, recommended_voice_id)` so the parent can
 * pre-select a sensible default voice. Falls back to a static list if
 * the fetch fails (older workspaces + offline dev).
 */
function LanguageSelect({ wsId, value, onChange, testid }) {
  const [langs, setLangs] = useState([]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/voice-agents/languages`)
      .then(({ data }) => setLangs(data?.rows || []))
      .catch(() => setLangs([
        { code: "en", label: "English" },
        { code: "hi", label: "Hindi" },
      ]));
  }, [wsId]);
  return (
    <select value={value || ""}
             onChange={(e) => {
               const code = e.target.value;
               const row = langs.find((l) => l.code === code);
               onChange(code, row?.elevenlabs_voice_id);
             }}
             className="h-10 w-full text-sm border border-border bg-background px-2 rounded-none"
             data-testid={testid}>
      {langs.length === 0 && <option value="">Loading languages…</option>}
      {langs.map((l) => (
        <option key={l.code} value={l.code}>
          {l.label}{l.voice_label ? ` · ${l.voice_label}` : ""}
        </option>
      ))}
    </select>
  );
}

